/**
 * getSizeNumeric — canonical helper to derive a numeric account size value.
 * Returns `size_numeric` when available, otherwise dynamically parses the string.
 *
 * Examples:
 *   getSizeNumeric({ size_numeric: 50000 })           → 50000
 *   getSizeNumeric({ size: "$50K" })                   → 50000
 *   getSizeNumeric({ size: "75K" })                    → 75000
 *   getSizeNumeric({ size: "$200K", size_numeric: 200000 }) → 200000
 */
export function getSizeNumeric(p) {
  if (p?.size_numeric != null) return p.size_numeric;
  return parseSizeString(p?.size);
}

/**
 * parseSizeString — parse a human-readable size string to numeric.
 * Handles "$50K", "50K", "$50,000", "50000", "$200K" etc.
 * Returns null if unparseable.
 */
export function parseSizeString(s) {
  if (!s) return null;
  const cleaned = String(s).replace(/[^0-9.KkMm]/g, '');
  if (!cleaned) return null;
  const upper = cleaned.toUpperCase();
  const kMatch = upper.match(/^([0-9.]+)K$/);
  if (kMatch) return Math.round(parseFloat(kMatch[1]) * 1000);
  const mMatch = upper.match(/^([0-9.]+)M$/);
  if (mMatch) return Math.round(parseFloat(mMatch[1]) * 1000000);
  const num = parseFloat(upper);
  return isNaN(num) ? null : Math.round(num);
}

/**
 * findByNumericSize — find a pricing entry matching a target numeric size.
 * Falls back to string comparison if size_numeric is absent.
 */
export function findByNumericSize(pricingArray, targetNumeric) {
  if (!pricingArray || !targetNumeric) return undefined;
  return pricingArray.find(p => getSizeNumeric(p) === targetNumeric)
    || pricingArray.find(p => parseSizeString(p.size) === targetNumeric);
}

/**
 * numToSizeLabel — convert a numeric size to a display label.
 * Examples: 50000 → "$50K", 100000 → "$100K", 75000 → "$75K"
 */
export function numToSizeLabel(n) {
  if (!n) return "N/A";
  if (n >= 1000) return `$${n / 1000}K`;
  return `$${n}`;
}
